import React, { useState, useEffect, useLayoutEffect, useRef } from "react";

// react-router
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";

// ui
import { Backdrop, Button } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { blue, green } from "@mui/material/colors";
import CircularProgress from "@mui/material/CircularProgress";

import { RequestLoadingModal } from "./component/RequestLoadingModal";

// jotai
import { useAtom, useSetAtom, useAtomValue } from "jotai";
import { AppAtoms } from "./lib/store";
import { useWindowSize } from "./lib/useWindowSize";

// api
import { getProfile } from "./api/profile";
import { getScreenColumns } from "./api/screenColumn";

// 画面一覧
import Account from "./pages/Account";
import AccountRunking from "./pages/AccountRunking";
import Contract from "./pages/Contract";
import Incentive from "./pages/Incentive";
import Product from "./pages/Product";
import ProductPartnerResult from "./pages/ProductPartnerResult";
import TossUp from "./pages/TossUp";
import TossUpForm from "./pages/TossUpForm";
import EntryForm from "./pages/EntryForm";
import Service from "./pages/Service";
import SaleCompany from "./pages/SaleCompany";
import Dashboard from "./pages/Dashboard";
import DataAnalysis from "./pages/DataAnalysis";
import FollowCall from "./pages/FollowCall";
import FollowCallConfig from "./pages/FollowCallConfig";
import ScreenColumnConfig from "./pages/ScreenColumnConfig";
import OutsideEntrySite from "./pages/OutsideEntrySite";
import OutsideEntryForm from "./pages/OutsideEntryForm";
/*
import ProductManual from "./pages/ProductManual";
*/
import Payment from "./pages/Payment";
import PaymentConfirm from "./pages/PaymentConfirm";
import Profile from "./pages/Profile";
import SystemConfig from "./pages/SystemConfig";
import Inquiry from "./pages/Inquiry";

import Login from "./pages/Login";

// コンポーネント
import TopBar from "./component/TopBar";

import { CONST } from "./config/const";

// 認証確認
import { fetchAuth, checkAuth } from "./lib/auth";

const theme = createTheme({
  palette: {
    primary: {
      light: blue[200],
      main: blue[400],
      dark: blue[700],
    },
  },
});

const useLatest = (value: any) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};
function App() {
  const [isLogin, setIsLogin] = useAtom(AppAtoms.isLogin);
  const lastIsLogin = useLatest(isLogin);
  const [loginUser, setLoginUser] = useAtom(AppAtoms.loginUser);
  const isLoading = useAtomValue(AppAtoms.isLoading);
  const setIsSmartPhone = useSetAtom(AppAtoms.isSmartPhone);
  const setPathname = useSetAtom(AppAtoms.pathname);
  const setContractScreenColumn = useSetAtom(AppAtoms.contractScreenColumn);
  const setTossUpScreenColumn = useSetAtom(AppAtoms.tossUpScreenColumn);
  const setAccountRunkingScreenColumn = useSetAtom(
    AppAtoms.accountRunkingScreenColumn,
  );
  const setTenantPlan = useSetAtom(AppAtoms.tenantPlanAtom);
  const { windowWidth, windowHeight } = useWindowSize();

  const [firstLoading, setFirstLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);

  const location = useLocation();
  const checkSession = async () => {
    if (lastIsLogin.current) {
      const serviceVersion = await checkAuth();
      if (CONST.serviceVersion !== serviceVersion) {
        // 画面更新要求
        setRequestLoading(true);
      }
    }
  };
  useEffect(() => {
    async function getScreenColumn() {
      const {
        contractScreenColumn,
        tossUpScreenColumn,
        accountRunkingScreenColumn,
      } = await getScreenColumns();
      setContractScreenColumn(contractScreenColumn);
      setTossUpScreenColumn(tossUpScreenColumn);
      setAccountRunkingScreenColumn(accountRunkingScreenColumn);
    }
    async function checkFetchAuth() {
      const { login, loginUser } = await fetchAuth();
      if (loginUser) {
        setLoginUser(loginUser);
        setIsLogin(login);
        getScreenColumn();
        setFirstLoading(false);
        const profile = await getProfile();
        setLoginUser({
          ...loginUser,
          db_authority: profile.authority,
          username: profile.account_name,
        });
        // プラン情報をatomにセット
        setTenantPlan(
          profile.plan_id != null
            ? {
                plan_id: profile.plan_id,
                plan_name: profile.plan_name ?? "",
                max_partners: profile.max_partners ?? 0,
                max_et_sites: profile.max_et_sites ?? 0,
                has_payment: profile.has_payment ?? false,
                has_ai_analysis: profile.has_ai_analysis ?? false,
                current_partners: profile.current_partners ?? 0,
                current_et_sites: profile.current_et_sites ?? 0,
                monthly_price: profile.monthly_price ?? 0,
              }
            : null,
        );
      }
      setFirstLoading(false);
      checkSession();
    }
    checkFetchAuth();
    setPathname(location.pathname);
  }, []);
  useLayoutEffect(() => {
    const isSmartPhone =
      windowWidth < 400 ||
      !!navigator.userAgent.match(/iPhone|Android.+Mobile/);
    setIsSmartPhone(isSmartPhone);
  }, [windowWidth]);

  const navigate = useNavigate();
  useEffect(() => {
    // 60秒ごとにチェック
    const intervalId = setInterval(checkSession, 60000);
    // コンポーネントのアンマウント時にクリーンアップ
    return () => clearInterval(intervalId);
  }, [navigate]);

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {firstLoading ? null : isLogin ? (
        <ThemeProvider theme={theme}>
          {requestLoading ? (
            <RequestLoadingModal onModal={requestLoading} />
          ) : null}
          <TopBar />
          <div style={{ display: "flex", width: "100%" }}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/toss_up" element={<TossUp />} />
              {loginUser?.db_authority !== "トスアップアカウント" ? (
                <Route path="/follow_call" element={<FollowCall />} />
              ) : null}
              {loginUser?.db_authority !== "トスアップアカウント" ? (
                <Route path="/contract" element={<Contract />} />
              ) : null}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/data-analysis" element={<DataAnalysis />} />
              {loginUser?.db_authority !== "営業アカウント" &&
              loginUser?.db_authority !== "トスアップアカウント" ? (
                <Route path="/runking" element={<AccountRunking />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ? (
                <Route path="/payment_admin" element={<Payment />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "パートナー" ? (
                <Route path="/payment" element={<PaymentConfirm />} />
              ) : null}
              {/*loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/top-company" element={<HighRankCompany />} />
              ) : null*/}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/product" element={<Product />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route
                  path="/product_partner_result"
                  element={<ProductPartnerResult />}
                />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/entry_form" element={<EntryForm />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/toss_up_form" element={<TossUpForm />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/service" element={<Service />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/incentive" element={<Incentive />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route
                  path="/follow_call_config"
                  element={<FollowCallConfig />}
                />
              ) : null}
              {loginUser?.db_authority !== "トスアップアカウント" ? (
                <Route
                  path="/outside_entry_site"
                  element={<OutsideEntrySite />}
                />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route
                  path="/outside_entry_form"
                  element={<OutsideEntryForm />}
                />
              ) : null}
              {loginUser?.db_authority !== "営業アカウント" &&
              loginUser?.db_authority !== "トスアップアカウント" ? (
                <Route path="/column_config" element={<ScreenColumnConfig />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ? (
                <Route path="/partner" element={<SaleCompany />} />
              ) : null}
              {loginUser?.authority === "パートナー" ? (
                <Route path="/config" element={<SaleCompany />} />
              ) : null}
              {loginUser?.authority === "マスターアカウント" ||
              loginUser?.authority === "スタッフアカウント" ||
              loginUser?.authority === "パートナー" ? (
                <Route path="/account" element={<Account />} />
              ) : null}
              <Route path="/profile" element={<Profile />} />
              {loginUser?.authority === "マスターアカウント" ? (
                <Route path="/system_config" element={<SystemConfig />} />
              ) : null}
              <Route path="/inquiry" element={<Inquiry />} />
            </Routes>
          </div>
        </ThemeProvider>
      ) : (
        <Login />
      )}
    </>
  );
}

export default App;
